import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../redux/reducers';
import { CommonState } from '../../redux/common/state/commonState';
import { ErrorType } from '../../models/HttpError';
import * as SettlementStore from '../../redux/middleware/SettlementMiddleware';
import * as ContractStore from '../../redux/middleware/ContractMiddleware';
import * as MedicalProtocolStore from '../../redux/middleware/MedicalProtocolMiddleware';
import { Settlement } from '../../models/Entities/Settlements/Settlement';
import { Contract } from '../../models/Entities/Contracts/Contract';
import { Protocol } from '../../models/Entities/Protocols/Protocol';

export interface SliceData<T> {
  list: T[];
  /** Cargando y todavía sin datos (con datos viejos se muestran igual). */
  loading: boolean;
  failed: boolean;
  error: ErrorType | undefined;
  reload: () => void;
}

/** Evita que varios widgets montados a la vez disparen la misma carga. */
const requestedAt = new Map<string, number>();
const REQUEST_DEDUPE_MS = 5000;

/** Sólo para tests. */
export const clearDashboardRequests = (): void => requestedAt.clear();

const useCommonSlice = <T>(key: string, select: (state: AppState) => CommonState<T> | undefined, load: () => unknown): SliceData<T> => {
  const dispatch = useDispatch();
  const slice = useSelector(select);
  const list = slice?.list ?? [];
  const loaded = !!slice?.successLoadingAll || list.length > 0;
  const loading = !!slice?.isLoadingAll;
  const failed = !!slice?.failOnLoadingAll;

  const reload = useCallback(() => {
    requestedAt.set(key, Date.now());
    dispatch(load() as any);
  }, [dispatch, key, load]);

  useEffect(() => {
    const last = requestedAt.get(key) ?? 0;
    if (!loaded && !loading && Date.now() - last > REQUEST_DEDUPE_MS) reload();
  }, [key, loaded, loading, reload]);

  return { list, loading: loading && !loaded, failed, error: slice?.error, reload };
};

const selectSettlements = (state: AppState) => state.Settlement as CommonState<Settlement> | undefined;
const selectContracts = (state: AppState) => state.Contract as CommonState<Contract> | undefined;
const selectProtocols = (state: AppState) => state.MedicalProtocols as CommonState<Protocol> | undefined;

export const useSettlements = (): SliceData<Settlement> =>
  useCommonSlice('settlements', selectSettlements, SettlementStore.actionCreators.GetAllSettlements);

export const useContracts = (): SliceData<Contract> =>
  useCommonSlice('contracts', selectContracts, ContractStore.actionCreators.GetAllContracts);

export const useProtocols = (): SliceData<Protocol> =>
  useCommonSlice('protocols', selectProtocols, MedicalProtocolStore.actionCreators.GetAllMedicalProtocols);

/** Usuario logueado (sub del perfil OIDC), para la preferencia por usuario. */
export const useCurrentUserId = (): string | undefined =>
  useSelector((state: AppState) => {
    const sub = state.oidc?.user?.profile?.sub;
    return sub ? String(sub) : undefined;
  });

/** Ancho del contenedor de la grilla (react-grid-layout necesita el ancho en píxeles). */
export const useMeasuredWidth = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return undefined;
    const update = () => setWidth(Math.round(element.getBoundingClientRect().width));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  return { ref, width };
};
